import React, { FC, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { getPublishedSites, downloadBlob } from '../../../api/third-party-publishing/ftp-requests';
import { FtpRequestSiteRecord } from '../../../types/third-party-publishing/ftp-request-site-record';

type SearchState = 'idle' | 'loading' | 'success' | 'error' | 'not-found';

const AdminThirdPartyPublishingTab: FC = () => {
    const [listingId, setListingId] = useState('');
    const [searchedId, setSearchedId] = useState('');
    const [state, setState] = useState<SearchState>('idle');
    const [sites, setSites] = useState<FtpRequestSiteRecord[]>([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [downloadingIds, setDownloadingIds] = useState<string[]>([]);
    const [downloadErrors, setDownloadErrors] = useState<{ [siteId: string]: string }>({});
    const inputRef = useRef<HTMLInputElement>(null);

    const canSearch = !isNaN(parseInt(listingId, 10)) && parseInt(listingId, 10) > 0;

    const onSearch = () => {
        if (!canSearch) { return; }
        const id = parseInt(listingId, 10);
        setState('loading');
        setSites([]);
        setErrorMessage('');
        setDownloadingIds([]);
        setDownloadErrors({});
        setSearchedId(listingId);

        getPublishedSites(id)
            .then((data) => {
                setSites(data);
                setState('success');
            })
            .catch((err: any) => {
                if (err && err.response && err.response.status === 404) {
                    setState('not-found');
                } else {
                    setErrorMessage((err && err.message) || 'An unexpected error occurred.');
                    setState('error');
                }
            });
    };

    const onDownload = (site: FtpRequestSiteRecord) => {
        if (!site.hasBlob || downloadingIds.includes(site.id)) { return; }
        setDownloadingIds(prev => [...prev, site.id]);
        setDownloadErrors(prev => ({ ...prev, [site.id]: '' }));

        const clearDownloadingId = () => {
            setDownloadingIds(prev => prev.filter(id => id !== site.id));
        };

        downloadBlob(site.id)
            .then(({ blob, fileName }) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                clearDownloadingId();
                setDownloadErrors(prev => ({ ...prev, [site.id]: '' }));
            })
            .catch((err: any) => {
                clearDownloadingId();
                const errMsg = (err && err.message) || 'Failed to download the file.';
                setDownloadErrors(prev => ({ ...prev, [site.id]: errMsg }));
            });
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') { onSearch(); }
    };

    const formatDate = (dateStr: string | null): string => {
        if (!dateStr) { return '\u2014'; }
        return new Date(dateStr).toLocaleString(undefined, {
            dateStyle: 'medium' as any,
            timeStyle: 'long' as any,
        });
    };

    const formatStatus = (status: string): string =>
        status.replace(/([A-Z])/g, ' $1').trim();

    const getStatusVariant = (statusCode: number): 'success' | 'error' | 'info' | 'default' => {
        if (statusCode === 13) { return 'success'; }
        if ([3, 4, 5, 7, 9, 11, 12, 14].includes(statusCode)) { return 'error'; }
        if ([1, 2, 6, 8, 10].includes(statusCode)) { return 'info'; }
        return 'default';
    };

    return (
        <Wrapper>
            {/* ── Search header ── */}
            <SearchHeader>
                <SearchHeaderInner>
                    <SearchHeaderTitle>Published Sites</SearchHeaderTitle>
                    <SearchBar>
                        <SearchIcon>
                            <FontAwesomeIcon icon={faSearch} />
                        </SearchIcon>
                        <SearchInput
                            ref={inputRef}
                            type="number"
                            min="1"
                            placeholder="Enter Listing ID…"
                            value={listingId}
                            onChange={e => setListingId(e.target.value)}
                            onKeyDown={onKeyDown}
                            autoComplete="off"
                        />
                        <SearchButton
                            onClick={onSearch}
                            disabled={!canSearch || state === 'loading'}
                        >
                            {state === 'loading' ? <Spinner /> : null}
                            {state === 'loading' ? 'Searching\u2026' : 'Search'}
                        </SearchButton>
                    </SearchBar>
                </SearchHeaderInner>
            </SearchHeader>

            {/* ── Results area ── */}
            <ContentArea fullWidth={state === 'idle' || state === 'not-found' || state === 'error' || state === 'success'}>

                {/* Landing placeholder */}
                {state === 'idle' && (
                    <EmptyCard>
                        <EmptyIcon>
                            <FontAwesomeIcon icon={faSearch} />
                        </EmptyIcon>
                        <EmptyTitle>Search published sites</EmptyTitle>
                        <EmptyMessage>
                            Enter a listing ID in the search box above to view published site records.
                        </EmptyMessage>
                    </EmptyCard>
                )}

                {/* Loading skeleton */}
                {state === 'loading' && (
                    <Card>
                        <CardHeader>
                            <SkBar style={{ width: 128 }} />
                            <SkBar style={{ width: 64, height: 10 }} />
                        </CardHeader>
                        {[1, 2, 3].map(i => (
                            <SkRow key={i}>
                                <SkBar style={{ width: 144 }} />
                                <SkBar style={{ width: 112 }} />
                                <SkBar style={{ width: 160 }} />
                                <SkBar style={{ width: 80, marginLeft: 'auto' }} />
                            </SkRow>
                        ))}
                    </Card>
                )}

                {/* Not found */}
                {state === 'not-found' && (
                    <EmptyCard>
                        <EmptyIcon>&#9888;</EmptyIcon>
                        <EmptyTitle>No published sites found</EmptyTitle>
                        <EmptyMessage>
                            Listing <strong>{searchedId}</strong> has no published records.
                        </EmptyMessage>
                    </EmptyCard>
                )}

                {/* Error */}
                {state === 'error' && (
                    <EmptyCard isError={true}>
                        <EmptyIcon>&#10005;</EmptyIcon>
                        <EmptyTitle>Request failed</EmptyTitle>
                        <EmptyMessage>{errorMessage}</EmptyMessage>
                    </EmptyCard>
                )}

                {/* Results table */}
                {state === 'success' && (
                    <Card>
                        <CardHeader>
                            <HeaderLabel>Listing {searchedId}</HeaderLabel>
                            <Badge>{sites.length} site{sites.length !== 1 ? 's' : ''}</Badge>
                        </CardHeader>
                        <Table>
                            <thead>
                                <tr>
                                    <Th style={{ width: '25%' }}>Site</Th>
                                    <Th style={{ width: '20%' }}>Action</Th>
                                    <Th style={{ width: '25%' }}>Status</Th>
                                    <Th style={{ width: '20%' }}>Published At</Th>
                                    <Th style={{ width: '10%' }} />
                                </tr>
                            </thead>
                            <tbody>
                                {sites.map(site => (
                                    <Tr key={site.id}>
                                        <Td>
                                            <SiteName>{site.siteName || '\u2014'}</SiteName>
                                        </Td>
                                        <Td>
                                            <ActionBadge action={site.publishAction}>
                                                {site.publishAction || '\u2014'}
                                            </ActionBadge>
                                        </Td>
                                        <Td>
                                            <StatusPill variant={getStatusVariant(site.statusCode)}>
                                                <StatusDot variant={getStatusVariant(site.statusCode)} />
                                                {formatStatus(site.status)}
                                            </StatusPill>
                                        </Td>
                                        <Td>
                                            <DateText>{formatDate(site.createdAt)}</DateText>
                                        </Td>
                                        <Td style={{ textAlign: 'right' }}>
                                            {site.hasBlob ? (
                                                <DownloadCell>
                                                    {downloadErrors[site.id] ? (
                                                        <>
                                                            <ErrorMsg>{downloadErrors[site.id]}</ErrorMsg>
                                                            <RetryButton onClick={() => onDownload(site)}>
                                                                Retry
                                                            </RetryButton>
                                                        </>
                                                    ) : (
                                                        <DownloadButton
                                                            onClick={() => onDownload(site)}
                                                            disabled={downloadingIds.includes(site.id)}
                                                        >
                                                            {downloadingIds.includes(site.id)
                                                                ? <><DlSpinner />Downloading&hellip;</>
                                                                : <><DownloadIcon aria-hidden="true">&#8595;</DownloadIcon><span>Download</span></>
                                                            }
                                                        </DownloadButton>
                                                    )}
                                                </DownloadCell>
                                            ) : (
                                                <NoBlob>&mdash;</NoBlob>
                                            )}
                                        </Td>
                                    </Tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card>
                )}
            </ContentArea>
        </Wrapper>
    );
};

/* ─── Animations ─────────────────────────────────────────────────────── */
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const shimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

/* ─── Layout ─────────────────────────────────────────────────────────── */
const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100%;
    background: #ffffff;
    font-family: 'Futura Md BT', helvetica, arial, sans-serif;
`;

const SearchHeader = styled.section`
    background: #ffffff;
    border-bottom: 1px solid #e4e7eb;
    padding: 18px 26px;
    position: sticky;
    top: 0;
    z-index: 10;
`;

const SearchHeaderInner = styled.div`
    max-width: 1180px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SearchHeaderTitle = styled.h2`
    font-size: 1.5rem;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0;
    letter-spacing: -0.01em;
`;

const SearchBar = styled.div`
    display: flex;
    align-items: center;
    background: #ffffff;
    border: 1px solid #d1d5db;
    border-radius: 12px;
    padding: 11px 16px;
    gap: 10px;
    max-width: 560px;
`;

const SearchIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    color: #6b7280;
    opacity: 1;
    flex-shrink: 0;
`;

const SearchInput = styled.input`
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 1rem;
    color: #1a1a1a;
    padding: 2px 0;
    min-width: 0;
    font-family: inherit;
    -moz-appearance: textfield;
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button { -webkit-appearance: none; }
    &::placeholder { color: #9ca3af; }
`;

const SearchButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    background: #006a4d;
    color: #ffffff;
    font-size: 0.9375rem;
    font-weight: 600;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    font-family: inherit;
    transition: background 150ms, opacity 150ms;
    &:hover:not(:disabled) { background: #005440; }
    &:disabled { opacity: 0.65; cursor: not-allowed; }
`;

const Spinner = styled.span`
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255,255,255,0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;

/* ─── Content ────────────────────────────────────────────────────────── */
const ContentArea = styled.main<{ fullWidth?: boolean }>`
    flex: 1;
    max-width: ${({ fullWidth }) => fullWidth ? '100%' : '1180px'};
    width: 100%;
    margin: ${({ fullWidth }) => fullWidth ? '0' : '0 auto'};
    padding: 18px 26px;
    box-sizing: border-box;
`;

const Card = styled.div`
    background: #ffffff;
    border-radius: 10px;
    border: 1px solid #e4e7eb;
    overflow: hidden;
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 20px;
    border-bottom: 1px solid #e4e7eb;
    background: #fafbfc;
`;

const HeaderLabel = styled.span`
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
`;

const Badge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    background: #d1f1e8;
    color: #005a3f;
    font-size: 0.6875rem;
    font-weight: 700;
    border-radius: 999px;
    letter-spacing: 0.02em;
`;

/* ─── Table ──────────────────────────────────────────────────────────── */
const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const Th = styled.th`
    padding: 12px 20px;
    text-align: left;
    font-size: 0.875rem;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    white-space: nowrap;
    background: #fafbfc;
    border-bottom: 1px solid #e4e7eb;
`;

const Tr = styled.tr`
    border-bottom: 1px solid #e4e7eb;
    &:last-child { border-bottom: none; }
    &:hover { background: #fafbfc; }
`;

const Td = styled.td`
    padding: 14px 20px;
    vertical-align: middle;
    font-size: 1.0625rem;
`;

const SiteName = styled.span`
    font-size: 1.125rem;
    font-weight: 600;
    color: #1a1a1a;
`;

const DateText = styled.span`
    font-size: 1rem;
    color: #6b7280;
    white-space: nowrap;
`;

/* ─── Status pill ────────────────────────────────────────────────────── */
const statusColors = {
    success: { bg: '#e8f5ef', text: '#006a4d', dot: '#00a657' },
    error:   { bg: '#fdecea', text: '#c0392b', dot: '#e74c3c' },
    info:    { bg: '#e8f0fe', text: '#1a56db', dot: '#3b82f6' },
    default: { bg: '#f0f0f0', text: '#555',    dot: '#999'    },
};

const StatusPill = styled.span<{ variant: keyof typeof statusColors }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 8px;
    border-radius: 999px;
    font-size: 0.875rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    white-space: nowrap;
    background: ${({ variant }) => statusColors[variant].bg};
    color: ${({ variant }) => statusColors[variant].text};
`;

const StatusDot = styled.span<{ variant: keyof typeof statusColors }>`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ variant }) => statusColors[variant].dot};
`;

/* ─── Action badge ───────────────────────────────────────────────────── */
const ActionBadge = styled.span<{ action?: string }>`
    display: inline-flex;
    align-items: center;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 600;
    white-space: nowrap;
    text-transform: capitalize;
    ${({ action }) => {
        const isPublish = action && action.toLowerCase() === 'publish';
        return `
            background: ${isPublish ? '#e8f5ef' : '#fff3cd'};
            color: ${isPublish ? '#006a4d' : '#856404'};
        `;
    }}
`;

/* ─── Download button ────────────────────────────────────────────────── */
const DownloadButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    padding: 7px 14px;
    min-width: 104px;
    background: #006a4d;
    color: #ffffff;
    font-size: 0.9375rem;
    font-weight: 600;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
    font-family: inherit;
    transition: background 140ms, opacity 140ms;
    &:hover:not(:disabled) { background: #005440; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const DownloadIcon = styled.span`
    font-size: 1.05em;
    font-weight: 800;
    line-height: 1;
`;

const DlSpinner = styled.span`
    display: inline-block;
    width: 12px;
    height: 12px;
    border: 2px solid rgba(255,255,255,0.4);
    border-top-color: #fff;
    border-radius: 50%;
    flex-shrink: 0;
    animation: ${spin} 0.7s linear infinite;
`;

const NoBlob = styled.span`
    font-size: 0.8125rem;
    color: #d1d5db;
`;

/* ─── Download cell with error handling ───────────────────────────────── */
const DownloadCell = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
`;

const ErrorMsg = styled.span`
    font-size: 0.75rem;
    color: #c0392b;
    font-weight: 500;
    max-width: 200px;
    text-align: right;
    word-break: break-word;
`;

const RetryButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 6px 12px;
    background: #e74c3c;
    color: #ffffff;
    font-size: 0.8125rem;
    font-weight: 600;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    white-space: nowrap;
    font-family: inherit;
    transition: background 140ms;
    &:hover { background: #c0392b; }
    &:active { background: #a93226; }
`;

/* ─── Skeleton ───────────────────────────────────────────────────────── */
const SkBar = styled.div`
    height: 14px;
    border-radius: 6px;
    background: linear-gradient(90deg, #e8e8e8 25%, #f4f4f4 50%, #e8e8e8 75%);
    background-size: 200% 100%;
    animation: ${shimmer} 1.6s infinite;
`;

const SkRow = styled.div`
    display: flex;
    align-items: center;
    gap: 48px;
    padding: 14px 20px;
    border-bottom: 1px solid #f0f0f0;
    &:last-child { border-bottom: none; }
`;

/* ─── Spinner ────────────────────────────────────────────────────────── */
const EmptyCard = styled.div<{ isError?: boolean }>`
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid ${({ isError }) => isError ? '#fee2e2' : '#e4e7eb'};
    padding: 64px 32px;
    text-align: center;
`;

const EmptyIcon = styled.div`
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    font-size: 20px;
    color: #6b7280;
`;

const EmptyTitle = styled.p`
    font-size: 1rem;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0 0 6px;
`;

const EmptyMessage = styled.p`
    font-size: 0.875rem;
    color: #888;
    line-height: 1.6;
    margin: 0;
`;

export default AdminThirdPartyPublishingTab;

